import React, { useEffect, useMemo, useState } from 'react';

import type {
  DevPlanState,
  NodeStatus,
  TrackCode,
} from '../TraineeDashboard/DevelopmentPlan/types';
import {
  buildPlanNodes,
  computeEdges,
  computeLayout,
  computeLayoutOcti,
  DEFAULT_WORLD,
  indexByCode,
  layoutModeFor,
  nodeStatus,
  octiWorld,
  unlockedGameKeys,
} from '../TraineeDashboard/DevelopmentPlan/helpers';
import SkillTreeCanvas from '../TraineeDashboard/DevelopmentPlan/components/SkillTreeCanvas';
import GameCard from '../TraineeDashboard/Puzzles/components/GameCard';
import type {
  CategoryId,
  Difficulty,
  Game,
} from '../TraineeDashboard/Puzzles/games';
import '../TraineeDashboard/DevelopmentPlan/developmentPlan.css';
import '../TraineeDashboard/Puzzles/puzzles.css';

type Props = {
  plan: DevPlanState;
  /** Node (internal_code) to center + select — the inspected item's node. */
  focusInternalCode?: string | null;
  /** DevAsset.key of an inspected mini game: overlays its Games-tab card. */
  gameAssetKey?: string | null;
  /** Extra bottom-left overlay (e.g. the solo room's grading card). */
  bottomLeftOverlay?: React.ReactNode;
};

/** Read-only render of the trainee's REAL skill tree — the same canvas (and the
 *  same derived-data pipeline) the trainee sees under Development Plan, fed by
 *  the observer devplan_state payload. Selection/focus work for exploration;
 *  play/launch/purchase affordances live in the detail panel, which is not
 *  mounted here, so the tree cannot trigger any trainee action. */
const DevPlanTreePreview: React.FC<Props> = ({
  plan,
  focusInternalCode,
  gameAssetKey,
  bottomLeftOverlay,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusTrack, setFocusTrack] = useState<TrackCode | null>(null);

  const nodes = useMemo(() => buildPlanNodes(plan), [plan]);
  const byCode = useMemo(() => indexByCode(nodes), [nodes]);
  const layoutMode = useMemo(() => layoutModeFor(plan.tracks), [plan.tracks]);
  const positions = useMemo(
    () =>
      layoutMode === 'octi'
        ? computeLayoutOcti(nodes, plan.tracks)
        : computeLayout(nodes, plan.tracks),
    [layoutMode, nodes, plan.tracks],
  );
  const world = useMemo(
    () => (layoutMode === 'octi' ? octiWorld(positions) : DEFAULT_WORLD),
    [layoutMode, positions],
  );
  const edges = useMemo(() => computeEdges(nodes), [nodes]);

  const statusByCode = useMemo(() => {
    const map: Record<string, NodeStatus> = {};
    nodes.forEach(n => {
      map[n.code] = nodeStatus(n, byCode, plan);
    });
    return map;
  }, [nodes, byCode, plan]);

  // Nodes with an unfinished solo room (plan.active_sessions) get the canvas's
  // "in progress" badge — same derivation as DevelopmentPlan.tsx, so the
  // observer sees the trainee's live rooms exactly as the trainee does.
  const activeNodeCodes = useMemo(() => {
    const codes = new Set<string>();
    (plan.active_sessions ?? []).forEach(session => {
      const node = nodes.find(
        candidate =>
          candidate.code === session.node_code ||
          candidate.internal_code === session.node_code,
      );
      if (node) codes.add(node.code);
    });
    return codes;
  }, [plan.active_sessions, nodes]);

  const trackColor = useMemo(() => {
    const map = {} as Record<TrackCode, string>;
    plan.tracks.forEach(t => {
      map[t.code] = t.color;
    });
    return map;
  }, [plan.tracks]);

  const counts = useMemo(() => {
    const map = {} as Record<TrackCode, { unlocked: number; total: number }>;
    plan.tracks.forEach(t => {
      map[t.code] = { unlocked: 0, total: 0 };
    });
    nodes.forEach(n => {
      map[n.track_code].total += 1;
      if (statusByCode[n.code]?.unlocked) map[n.track_code].unlocked += 1;
    });
    return map;
  }, [plan.tracks, nodes, statusByCode]);

  // The inspected node, as the canvas's display code: centered AND selected.
  const centerOnCode = useMemo(() => {
    if (!focusInternalCode) return null;
    return (
      nodes.find(n => n.internal_code === focusInternalCode)?.code ?? null
    );
  }, [nodes, focusInternalCode]);

  useEffect(() => {
    setSelectedId(centerOnCode);
  }, [centerOnCode]);

  // Inspected mini game → its Games-tab card, built exactly like the Puzzles
  // grid builds real devplan cards (same status semantics, locks enforced).
  const game: Game | null = useMemo(() => {
    if (!gameAssetKey) return null;
    const asset = plan.assets.find(
      a => a.key === gameAssetKey && a.kind === 'game',
    );
    if (!asset) return null;
    const unlocked = unlockedGameKeys(plan, true);
    return {
      id: `dp:${asset.key}`,
      title: asset.title,
      blurb: asset.blurb || '',
      category: (asset.category || undefined) as CategoryId | undefined,
      difficulty: (asset.difficulty || undefined) as Difficulty | undefined,
      minutes: asset.minutes ?? undefined,
      skills: asset.skills || undefined,
      icon: asset.icon || 'experiment',
      status: unlocked.has(asset.key) ? 'new' : 'locked',
      backing: 'devplan',
      assetKey: asset.key,
      filePath: asset.file_path,
    };
  }, [plan, gameAssetKey]);

  return (
    <div className="devplan cstudio__devplan-preview">
      <div className="devplan__body">
        <SkillTreeCanvas
          nodes={nodes}
          tracks={plan.tracks}
          byCode={byCode}
          positions={positions}
          edges={edges}
          statusByCode={statusByCode}
          activeNodeCodes={activeNodeCodes}
          trackColor={trackColor}
          counts={counts}
          selectedId={selectedId}
          coreSelected={false}
          focusTrack={focusTrack}
          onFocus={setFocusTrack}
          onSelectNode={setSelectedId}
          onSelectCore={() => {}}
          onBackground={() => setSelectedId(null)}
          world={world}
          layoutMode={layoutMode}
          centerOnCode={centerOnCode}
        />
      </div>
      {game && (
        <div className="puzzles cstudio__game-overlay">
          <GameCard game={game} onLaunch={() => {}} />
        </div>
      )}
      {bottomLeftOverlay && (
        <div className="cstudio__grading-overlay">{bottomLeftOverlay}</div>
      )}
    </div>
  );
};

export default DevPlanTreePreview;
